import { cn } from '@/lib/utils';
import {
  BuildingOffice2Icon,
  CalendarIcon,
  HeartIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';
import {
  BuildingOffice2Icon as BuildingOffice2SolidIcon,
  CalendarIcon as CalendarSolidIcon,
  HeartIcon as HeartSolidIcon,
  HomeIcon as HomeSolidIcon,
} from '@heroicons/react/24/solid';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { hcVerbiage, Language } from '../../common/verbiage';

type Props = {
  basePath: string;
  language: Language;
  // Essentials clinics (`ehr: false`) hide Appts + Balance — those
  // features aren't connected to anything for them.
  hasEhr?: boolean;
};

type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  activeIcon: React.ComponentType<{ className?: string }>;
  activePaths: string[];
};

const matchesTab = (pathname: string, activePaths: string[]): boolean => {
  return activePaths.some(p => pathname === p || pathname.startsWith(`${p}/`));
};

/**
 * Desktop / large-tablet sidebar mirror of BottomNav. Same routes, same
 * "active section" logic — just rendered as a sticky vertical rail on
 * the left so the wide-screen layout doesn't waste horizontal space on
 * an oversized BottomNav. Hidden below `lg` (BottomNav handles those
 * viewports).
 */
const SideNav: React.FC<Props> = ({ basePath, language, hasEhr = true }) => {
  const { pathname } = useLocation();
  const b = basePath || '';

  const navItems: NavItem[] = [
    {
      to: basePath || '/',
      label: hcVerbiage.home[language],
      icon: HomeIcon,
      activeIcon: HomeSolidIcon,
      activePaths: [basePath || '/'],
    },
    {
      to: `${b}/health`,
      label: hcVerbiage.health[language],
      icon: HeartIcon,
      activeIcon: HeartSolidIcon,
      activePaths: [`${b}/health`, `${b}/exercises`, `${b}/conditions`, `${b}/advice`],
    },
    ...(hasEhr
      ? [
          {
            to: `${b}/appointments`,
            label: language === 'es' ? 'Citas' : 'Appointments',
            icon: CalendarIcon,
            activeIcon: CalendarSolidIcon,
            activePaths: [`${b}/appointments`, `${b}/schedule`, `${b}/thank-you`],
          },
          // Balance tab is intentionally hidden for now — the
          // pay-balance / saved-cards / add-card flows aren't ready
          // to ship. Restore this entry once those land.
          // {
          //   to: `${b}/balance`,
          //   label: hcVerbiage.balance[language],
          //   icon: CreditCardIcon,
          //   activeIcon: CreditCardSolidIcon,
          //   activePaths: [`${b}/balance`],
          // },
        ]
      : []),
    {
      to: `${b}/clinic`,
      label: hcVerbiage.clinic[language],
      icon: BuildingOffice2Icon,
      activeIcon: BuildingOffice2SolidIcon,
      activePaths: [`${b}/clinic`],
    },
  ];

  return (
    <nav
      aria-label="Primary"
      // The parent <aside> handles `hidden lg:block` and column width;
      // here we just make the rail itself sticky beneath the header so
      // it stays visible as the main content scrolls.
      className="sticky top-24 flex flex-col gap-1"
    >
      {navItems.map(item => {
        const isActive = matchesTab(pathname, item.activePaths);
        const Icon = isActive ? item.activeIcon : item.icon;

        return (
          <Link
            key={item.to}
            to={item.to}
            className={cn(
              'group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
              isActive
                ? 'bg-[color-mix(in_srgb,var(--primary-color)_12%,transparent)] text-[var(--primary-color)]'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/5'
            )}
          >
            <Icon className="h-5 w-5 shrink-0" />
            <span className="truncate">{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
};

export default SideNav;
